type FromStringBuilder<T> = (_str: string) => T;

// eslint-disable-next-line @typescript-eslint/no-extraneous-class
export default class QueryUtil {
  private static mock = new URL(document.location.href);

  /**
   * Attempt to build an instance of T.
   *
   * Attempt to build an instance of T via the builder or else return the default value.
   *
   * e.g. `return builder(document.location.search[queryPara]) || defaultValue`
   */
  public static buildOrDefault<T>({
    builder,
    queryParam,
    defaultVal,
  }: {
    builder: FromStringBuilder<T>;
    queryParam: string;
    defaultVal: T;
  }): T {
    QueryUtil.mock.searchParams.set(queryParam, '');

    const queryVal = new URL(document.location.href).searchParams.get(queryParam) || '';
    if (!queryVal) return defaultVal;

    try {
      const t: T = builder(queryVal);
      return t || defaultVal;
    } catch {
      return defaultVal;
    }
  }

  /** return as string or default */
  public static stringOrDefault<T>({
    queryParam,
    defaultVal,
  }: {
    queryParam: string;
    defaultVal: T;
  }): T | string {
    return QueryUtil.buildOrDefault<T | string>({
      queryParam,
      defaultVal,
      builder: (str: string) => str,
    });
  }

  /** convert true/false to bool */
  public static boolOrDefault<T>({
    queryParam,
    defaultVal,
  }: {
    queryParam: string;
    defaultVal: T;
  }): T | boolean {
    return QueryUtil.buildOrDefault<T | boolean>({
      queryParam,
      defaultVal,
      builder: (str: string) => {
        if (str === 'true') return true;
        if (str === 'false') return false;
        return defaultVal;
      },
    });
  }

  /**
   * Build from query string array. e.g. `foo=bar,baz` becomes
   * `bar,baz`.split(',).map((str) => builder(str))`
   */
  public static buildArrayOrDefault<T>({
    defaultVal,
    queryParam,
    builder,
  }: {
    defaultVal: Array<T>;
    queryParam: string;
    builder: FromStringBuilder<T>;
  }): Array<T> {
    QueryUtil.mock.searchParams.set(queryParam, '');

    const queryVal = new URL(document.location.href).searchParams.get(queryParam) || '';
    if (!queryVal) return defaultVal;

    try {
      const bits = queryVal.split(',');
      const t: Array<T> = bits.map((str) => builder(str));
      return t || defaultVal;
    } catch {
      return defaultVal;
    }
  }

  /** return `mapping[queryVal] as T` or default */
  public static buildFromMappingOrDefault<T>({
    defaultVal,
    queryParam,
    mapping,
  }: {
    defaultVal: T;
    queryParam: string;
    mapping: Record<string, T>;
  }): T {
    QueryUtil.mock.searchParams.set(queryParam, '');

    const queryVal = new URL(document.location.href).searchParams.get(queryParam) || '';
    if (!queryVal) return defaultVal;

    const found = mapping[queryVal];
    if (!found) return defaultVal;

    return found;
  }

  /**
   * return `queryVal as T || defaultVal`
   */
  public static identityOrDefault<T, D>({
    defaultVal,
    queryParam,
  }: {
    defaultVal: D;
    queryParam: string;
  }) {
    return QueryUtil.buildOrDefault<T | D>({
      queryParam,
      defaultVal,
      builder: (str: string) => str as unknown as T,
    });
  }

  public static logDebug() {
    console.log(QueryUtil.mock.href);
  }
}
